/**
 * Accountant Reconciliation Page
 * ================================
 * Forensic accounting interface for P&L reconciliation.
 *
 * Two modes:
 * 1. Year-at-a-glance P&L grid (default)
 * 2. Cell drill-down reconciliation view
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import { useAuth } from '../contexts/TestAuthContext';
import AccountantLayout from '../components/AccountantLayout';
import PLReconciliationGrid from '../components/accountant/PLReconciliationGrid';
import ReconciliationPanel from '../components/accountant/ReconciliationPanel';
import api from '../utils/api';
import { ArrowLeft, FileSpreadsheet } from 'lucide-react';

interface PLGridData {
  report_id: string;
  years: number[];
  pl_structure: any;
  pl_grid: any;
  reconciliation_status: Record<string, string>;
  catch_all_items: Array<[string, string]>;
  data_source?: 'transactions' | 'pl_summary' | 'none';
  drill_down_available?: boolean;
}

export default function AccountantReconciliation() {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { accountant, loading: authLoading } = useAccountantAuth();
  const { user } = useAuth();

  // In test mode, bypass accountant auth requirement
  const isTestMode = process.env.REACT_APP_DISABLE_AUTH === 'true';

  // Memoize the test accountant to prevent re-render loops
  const testAccountant = React.useMemo(() => ({
    id: 'test_user_123',
    email: 'test@claimsync.com',
    full_name: 'Test Accountant'
  }), []);

  // Support Clerk-authed users coming from AccountantQueue
  const clerkAccountant = React.useMemo(() => user ? ({
    id: user.sub || user.userId || user.id || 'clerk-user',
    email: user.email || '',
    full_name: user.name || user.fullName || user.email || 'Staff'
  }) : null, [user]);

  const effectiveAccountant = isTestMode ? testAccountant : (accountant || clerkAccountant);
  const effectiveAuthLoading = isTestMode ? false : (authLoading && !user);

  const [plGridData, setPlGridData] = useState<PLGridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Drill-down state
  const [selectedCell, setSelectedCell] = useState<{
    year: number;
    month: number;
    section: string;
    line_item: string;
  } | null>(null);

  useEffect(() => {
    // Wait for auth context to finish loading
    if (effectiveAuthLoading) {
      return;
    }
    
    if (!effectiveAccountant) {
      navigate('/accountant/login');
      return;
    }

    if (!reportId) {
      setError('No report ID provided');
      setLoading(false);
      return;
    }

    fetchPLGrid();
  }, [reportId, effectiveAccountant, effectiveAuthLoading]);

  // Check URL params for drill-down
  useEffect(() => {
    const year = searchParams.get('year');
    const month = searchParams.get('month');
    const section = searchParams.get('section');
    const lineItem = searchParams.get('line_item');

    if (year && month && section && lineItem) {
      setSelectedCell({
        year: parseInt(year),
        month: parseInt(month),
        section: decodeURIComponent(section),
        line_item: decodeURIComponent(lineItem)
      });
    }
  }, [searchParams]);

  const fetchPLGrid = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Fetching P&L grid for report:', reportId);
      // api.get returns data directly, not wrapped in response object
      const data = await api.get(`/api/v1/reconciliation/pl-grid/${reportId}`);
      console.log('P&L grid response:', data);
      setPlGridData(data);
    } catch (err: any) {
      console.error('Error fetching P&L grid:', err);
      setError(err.message || 'Failed to load P&L grid');
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (year: number, month: number, section: string, lineItem: string) => {
    // Check if drill-down is available
    if (plGridData && plGridData.data_source !== 'transactions') {
      // Show info message for P&L summary data
      setInfoMessage('This report was created using P&L summary data. Transaction-level drill-down is not available, but you can still view the aggregated amounts in the grid.');
      return;
    }
    
    // Update URL params
    const params = new URLSearchParams({
      year: year.toString(),
      month: month.toString(),
      section: section,
      line_item: lineItem
    });
    navigate(`/accountant/reconciliation/${reportId}?${params.toString()}`);

    setSelectedCell({ year, month, section, line_item: lineItem });
  };

  const handleBackToGrid = () => {
    navigate(`/accountant/reconciliation/${reportId}`);
    setSelectedCell(null);
  };

  const handleExportWorkPaper = async () => {
    if (!plGridData) return;

    try {
      // Show confirmation with preview
      const confirmExport = window.confirm(
        'Export Forensic Reconciliation Work Paper\n\n' +
        '📊 This will generate a multi-sheet Excel workbook containing:\n\n' +
        '  Sheet 1: P&L Reconciliation Summary\n' +
        '  Sheet 2: Transaction Detail (All ' + (plGridData.years.length * 12) + ' cells)\n' +
        '  Sheet 3: Anomalies & Flagged Items\n' +
        '  Sheet 4: Reclassification Audit Trail\n' +
        '  Sheet 5: Variance Analysis by Line Item\n' +
        '  Sheet 6: Sign-off Documentation\n\n' +
        '📁 Format: Professional P&L Template (Court-Ready)\n' +
        '👤 Prepared by: ' + (accountant?.full_name || 'Unknown') + '\n' +
        '📅 Date: ' + new Date().toLocaleDateString() + '\n\n' +
        'Continue with export?'
      );

      if (!confirmExport) return;

      // Show loading indicator
      setStatusMessage('Generating workpaper... This may take a few moments for large reports.');

      // Call API to generate Excel file
      const response = await api.get(`/api/v1/reconciliation/export/${reportId}`, {
        responseType: 'blob'
      });

      // Download the file
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Reconciliation_Workpaper_${reportId.substring(0, 8)}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      setStatusMessage('Workpaper exported successfully! The file has been downloaded to your device.');
    } catch (err: any) {
      console.error('Error exporting workpaper:', err);

      // If report not found, show helpful message
      if (err.response?.status === 404) {
        setExportError('Export unavailable for this report. Ensure the case has uploaded financial documents, processed transactions, and P&L categorization complete.');
      } else {
        setExportError('Failed to export workpaper: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  if (loading) {
    return (
      <AccountantLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading P&L reconciliation...</p>
          </div>
        </div>
      </AccountantLayout>
    );
  }

  if (error) {
    return (
      <AccountantLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <h2 className="text-red-800 font-bold text-lg mb-2">Error</h2>
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => navigate('/accountant/dashboard')}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </AccountantLayout>
    );
  }

  if (!plGridData) {
    return null;
  }

  // Check if this is P&L summary data (no transaction drill-down)
  const isPLSummary = plGridData.data_source === 'pl_summary' || plGridData.drill_down_available === false;
  const hasNoData = plGridData.data_source === 'none';

  return (
    <AccountantLayout>
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* P&L Summary Banner */}
        {isPLSummary && !hasNoData && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <div className="text-amber-600 text-xl">📊</div>
            <div>
              <h3 className="font-semibold text-amber-800">P&L Summary View</h3>
              <p className="text-amber-700 text-sm">
                This report was created from P&L summary data. The grid shows monthly aggregates, 
                but individual transaction drill-down is not available. To enable transaction-level 
                reconciliation, bank statement data would need to be uploaded.
              </p>
            </div>
          </div>
        )}
        
        {/* No Data Banner */}
        {hasNoData && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <div className="text-gray-500 text-xl">📭</div>
            <div>
              <h3 className="font-semibold text-gray-800">No Financial Data</h3>
              <p className="text-gray-600 text-sm">
                This report does not contain financial data for reconciliation. 
                Please ensure P&L statements or bank statements have been uploaded.
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-600 text-white rounded-lg p-6 mb-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {selectedCell && (
                <button
                  onClick={handleBackToGrid}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  title="Back to P&L Grid"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <div>
                <h1 className="text-2xl font-bold mb-1">
                  {selectedCell
                    ? `Reconciliation: ${selectedCell.line_item}`
                    : 'P&L Reconciliation'}
                  {isPLSummary && !selectedCell && (
                    <span className="ml-2 text-sm font-normal bg-amber-500/30 px-2 py-1 rounded">
                      Summary View
                    </span>
                  )}
                </h1>
                <p className="text-blue-100 text-sm">
                  {selectedCell
                    ? `${new Date(selectedCell.year, selectedCell.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} • ${selectedCell.section}`
                    : `Report ID: ${reportId?.substring(0, 8)}...`}
                </p>
              </div>
            </div>

            <button
              onClick={handleExportWorkPaper}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition-colors"
            >
              <FileSpreadsheet size={20} />
              Export Work Paper
            </button>
          </div>
        </div>

        {/* Main Content */}
        {selectedCell ? (
          <ReconciliationPanel
            reportId={reportId!}
            year={selectedCell.year}
            month={selectedCell.month}
            section={selectedCell.section}
            lineItem={selectedCell.line_item}
            onBack={handleBackToGrid}
          />
        ) : (
          <PLReconciliationGrid
            plGridData={plGridData}
            onCellClick={handleCellClick}
          />
        )}
      </div>
    </AccountantLayout>
  );
}
